export interface MilitaryTime {
  hour: number;
  minute: number;
}

export interface TimeRange {
  start: TimeRangeItem;
  end: TimeRangeItem;
}

export interface TimeRangeItem {
  hours: number;
  minutes: number;
  period: string;
  label: string;
  milliseconds: number;
}

/**
 * Convert from default "8:00 pm" to military time "20:00"
 *
 * @param time Time that should be converted (e.g. "8:00 pm")
 * @returns MilitaryTime - Converted Time
 */
export function toMilitaryTime(time: string): MilitaryTime {
  const [hourStr, minuteStr] = time.split(':');
  const hour: number = parseInt(hourStr, 10);
  const minute: number = parseInt(minuteStr, 10);

  let hourInt: number = hour;

  if (time.toLowerCase().includes('pm') && hour !== 12) hourInt += 12;
  if (time.toLowerCase().includes('am') && hour === 12) hourInt = 0;

  return { hour: hourInt, minute };
}

/**
 * Convert from default "8:00 pm" to minutes 1200
 *
 * @param time Time that should be converted (e.g. "8:00 pm")
 * @returns number - Minutes
 */
export function parseTimeToMinutes(time: string): number {
  const { hour, minute } = toMilitaryTime(time);
  return hour * 60 + minute;
}

/**
 * Checks whether the restaurant is open for a given date/time.
 *
 * @param start_time Start Hours (e.g. "8:00 am")
 * @param close_time Close Working Hours (e.g. "9:00 pm")
 * @param date Is Date Between
 * @param timezone Optional timezone string (e.g. "America/New_York")
 *
 * @returns boolean - true if in range, false if out of range
 */
export function isTimeInRange(
  start_time?: string,
  close_time?: string,
  date: Date = new Date(),
  timezone?: string
): boolean {
  if (!start_time || !close_time) {
    return false;
  }

  let currentTimeInMinutes: number;
  
  // If timezone is provided, get the current time in that timezone
  if (timezone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    });
    const parts = formatter.formatToParts(date);
    const hours = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    const minutes = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
    currentTimeInMinutes = hours * 60 + minutes;
  } else {
    currentTimeInMinutes = date.getHours() * 60 + date.getMinutes();
  }

  const openTime: number = parseTimeToMinutes(start_time || '');
  const closeTime: number = parseTimeToMinutes(close_time || '');

  return currentTimeInMinutes >= openTime && currentTimeInMinutes <= closeTime;
}

/**
 * Get time range in military time
 *
 * @param timeRange Time Range (e.g. "10:00 AM - 11:00 AM")
 * @returns TimeRange
 */
export function parseTimeRange(timeRange: string): TimeRange | null {
  const regex = /(\d{1,2}:\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)/i;
  const match: RegExpMatchArray | null = timeRange.match(regex);

  if (!match) {
    console.error('Invalid time range');
    return null;
  }

  const [, startTime, startPeriod, endTime, endPeriod] = match; // fix here

  const [startTimeHours, startTimeMinutes] = startTime.split(':').map(Number);
  const [endTimeHours, endTimeMinutes] = endTime.split(':').map(Number);

  function toMilliseconds(
    hours: number,
    minutes: number,
    period: string
  ): number {
    let h = hours;
    if (period.toUpperCase() === 'AM' && h === 12) h = 0;
    if (period.toUpperCase() === 'PM' && h !== 12) h += 12;
    return h * 60 * 60 * 1000 + minutes * 60 * 1000;
  }

  return {
    start: {
      hours: startTimeHours,
      minutes: startTimeMinutes,
      period: startPeriod.toUpperCase() as 'AM' | 'PM',
      label: `${startTime} ${startPeriod}`,
      milliseconds: toMilliseconds(
        startTimeHours,
        startTimeMinutes,
        startPeriod
      )
    },
    end: {
      hours: endTimeHours,
      minutes: endTimeMinutes,
      period: endPeriod.toUpperCase() as 'AM' | 'PM',
      label: `${endTime} ${endPeriod}`,
      milliseconds: toMilliseconds(endTimeHours, endTimeMinutes, endPeriod)
    }
  };
}

/** Merge adjacent time ranges into combined readable strings */
export function mergeAdjacentTimeRanges(timeRanges: TimeRange[]): string[] {
  if (timeRanges.length === 0) return [];

  const sorted: TimeRange[] = [...timeRanges].sort(
    (a: TimeRange, b: TimeRange): number => {
      return a.start.milliseconds - b.start.milliseconds;
    }
  );
  const merged: { start: TimeRange['start']; end: TimeRange['end'] }[] = [];

  let current = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];
    if (current.end.milliseconds === next.start.milliseconds) {
      current = { start: current.start, end: next.end };
    } else {
      merged.push({ start: current.start, end: current.end });
      current = next;
    }
  }

  merged.push({ start: current.start, end: current.end });

  return merged.map((r) => `${formatTime(r.start)} - ${formatTime(r.end)}`);
}

export function getTimeDiapasonByTime(
  time: Date,
  diapasons: string[]
): string | undefined {
  return diapasons.find((diapason) => {
    const [startTimeStr, endTimeStr] = diapason.split('-').map((s) => s.trim());
    const start = toMilitaryTime(startTimeStr);
    const end = toMilitaryTime(endTimeStr);

    const timeMinutes = time.getHours() * 60 + time.getMinutes();
    const startMinutes = start.hour * 60 + start.minute;
    const endMinutes = end.hour * 60 + end.minute;

    return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
  });
}

/** Format time nicely like "9:00 AM" */
function formatTime(time: {
  hours: number;
  minutes: number;
  period: string;
}): string {
  const h: number = time.hours % 12 === 0 ? 12 : time.hours % 12;
  const m: string = time.minutes.toString().padStart(2, '0');
  const p: string = time.period.toUpperCase();
  return `${h}:${m} ${p}`;
}
